import { oc } from "@orpc/contract";
import { z } from "zod";
import { CalculationTypeSchema } from "@erp/shared-types";
import { MonthOverrideSchema } from "./month-override.contract";

// ============================================================================
// Shared Forecast Schemas
// ============================================================================

const RebateBreakdownSchema = z.object({
  supplierId: z.string(),
  supplierName: z.string(),
  divisionId: z.string(),
  divisionName: z.string(),
  ruleId: z.string(),
  ruleName: z.string(),
  agreementId: z.string(),
  agreementName: z.string(),
  rebateAmount: z.number(),
  cumulativeRebateAmount: z.number().optional(),
  totalVolume: z.number().optional(),
  affectedVolume: z.number().optional(),
  qualifyingVolume: z.number().optional(),
  tierApplied: z.number().optional(),
  rebatePercentage: z.number().optional(),
  invoiceCount: z.number().optional(),
  calculationType: z.string(),
  isDeactivated: z.boolean().optional(),
  // Frozen categorization signals (REPORTED rows only); see RebateRuleSnapshot.
  category: z
    .enum(["Redistribution", "Conditional", "Non-Conditional"])
    .optional(),
  isConditional: z.boolean().optional(),
  ruleTagIds: z.array(z.string()).optional(),
});

const RebatePointSchema = z.object({
  date: z.date(), // oRPC wire format handles Date serialization natively
  daysFromStart: z.number(),
  totalRebateAmount: z.number(),
  totalCumulativeRebateAmount: z.number(),
  totalVolume: z.number().optional(),
  totalAffectedVolume: z.number().optional(),
  breakdowns: z.array(RebateBreakdownSchema),
});

const OverallRebateMetricsSchema = z.object({
  totalRebateAmount: z.number(),
  totalVolume: z.number(),
  rebateableVolume: z.number(),
  affectedVolume: z.number(),
  rebatePercentage: z.number(),
});

const TagSchema = z.object({
  id: z.string(),
  name: z.string(),
});

/**
 * Schema matching UnifiedForecastResult from @erp/shared-types.
 * Date fields use z.date() - oRPC's wire format handles Date serialization natively,
 * so the client receives actual Date objects (not strings).
 */
const UnifiedForecastResultSchema = z.object({
  forecastPeriod: z.object({
    startDate: z.date(),
    endDate: z.date(),
    totalDays: z.number(),
    description: z.string(),
  }),
  estimates: z.array(RebatePointSchema),
  predictions: z.record(z.string(), z.array(RebatePointSchema)),
  reported: z.array(RebatePointSchema),
  // Total purchase volume per (supplier, division, agreement, month) from
  // invoice line items. See UnifiedForecastResult.totalVolumeSeries.
  totalVolumeSeries: z.array(RebatePointSchema).optional(),
  overallMetrics: z
    .object({
      actual: OverallRebateMetricsSchema,
      predicted: OverallRebateMetricsSchema,
    })
    .optional(),
  rules: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      thresholdType: z.string().nullable(),
      isManual: z.boolean(),
      isActive: z.boolean(),
      agreementId: z.string(),
      isConditional: z.boolean(),
      isPrimaryBucket: z.boolean().optional(),
      tags: z.array(TagSchema).optional(),
    }),
  ),
  agreements: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      supplierId: z.string(),
      supplierName: z.string(),
      tags: z.array(TagSchema).optional(),
    }),
  ),
  suppliers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      code: z.string().nullable().optional(),
      tags: z.array(TagSchema).optional(),
    }),
  ),
  divisions: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      code: z.string().nullable().optional(),
    }),
  ),
  reportedAlgorithm: z.string(),
  forecastDates: z.array(z.date()), // oRPC delivers actual Date objects
  // User-set per-(agreement, month) forecast overrides (e.g. true-zero months).
  monthOverrides: z.array(MonthOverrideSchema),
});

// ============================================================================
// Fetch Forecast (stored data from DB)
// ============================================================================

export const FetchForecastRequestSchema = z.object({
  agreementId: z.string().uuid().optional(),
  year: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  calculationTypes: z.array(CalculationTypeSchema).optional(),
});

export const FetchForecastResponseSchema = z.object({
  data: UnifiedForecastResultSchema,
});

// ============================================================================
// Generate Forecast for Agreement (auto-generates if stale)
// ============================================================================

export const GenerateForecastForAgreementRequestSchema = z.object({
  agreementId: z.string().uuid("Invalid agreement ID format"),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export const GenerateForecastForAgreementResponseSchema = z.object({
  data: UnifiedForecastResultSchema,
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const forecastContracts = {
  fetch: oc
    .input(FetchForecastRequestSchema)
    .output(FetchForecastResponseSchema)
    .route({
      method: "GET",
      summary: "Get the rebate forecast",
      description:
        "Returns stored rebate estimates, predictions, and reported amounts by month, with breakdowns by supplier, division, agreement, and rule. Filter by agreement, year, date range, or calculation type.",
    }),

  generateForAgreement: oc
    .input(GenerateForecastForAgreementRequestSchema)
    .output(GenerateForecastForAgreementResponseSchema)
    .route({
      method: "POST",
      summary: "Refresh and get an agreement's rebate forecast",
      description:
        "Regenerates an active rebate agreement's forecast if stale, then returns its estimates, predictions, and reported amounts by month with rule and division breakdowns.",
    }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type FetchForecastRequest = z.infer<typeof FetchForecastRequestSchema>;
export type FetchForecastResponse = z.infer<typeof FetchForecastResponseSchema>;
export type GenerateForecastForAgreementRequest = z.infer<
  typeof GenerateForecastForAgreementRequestSchema
>;
export type GenerateForecastForAgreementResponse = z.infer<
  typeof GenerateForecastForAgreementResponseSchema
>;
